import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, ModalLayout, SearchInput, Skeleton, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { NO_HEADER_LINE, WIDE_MODAL } from '../shared/modalClasses';
import { useMemberCandidates } from '../../hooks/useMemberCandidates';
import StatusBadge from '../shared/StatusBadge';

/**
 * "Add members" modal: searches the workspace members that are not in the group and adds the
 * selection. Search commits on Enter. Failure keeps the modal and the selection.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {string} props.groupId
 * @param {string} props.groupName
 * @param {() => void} props.onClose
 * @param {(memberIds: string[]) => Promise<void>} props.onAdd Rejects with the API message.
 */
function AddMembersModal({ isOpen, groupId, groupName, onClose, onAdd }) {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const { candidates, isLoading, error, search, setSearch } = useMemberCandidates(groupId, {
    enabled: isOpen,
  });
  const [draft, setDraft] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (isOpen) return;
    setDraft('');
    setSearch('');
    setSelected(new Set());
  }, [isOpen, setSearch]);

  const handleClose = useCallback(() => {
    if (!submittingRef.current) onClose();
  }, [onClose]);

  const toggle = useCallback((memberId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }, []);

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') setSearch(draft.trim());
    },
    [draft, setSearch],
  );

  const handleClearSearch = useCallback(() => {
    setDraft('');
    setSearch('');
  }, [setSearch]);

  const handleAdd = useCallback(async () => {
    if (selected.size === 0 || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const ids = [...selected];
    try {
      await onAdd(ids);
      toast.success({
        title: t('groups.addMembers.successTitle'),
        message: t('groups.addMembers.successMessage', { count: ids.length, name: groupName }),
        canClose: true,
      });
      onClose();
    } catch (err) {
      toast.error({
        title: t('groups.addMembers.errorTitle'),
        message: err?.message ?? t('groups.errors.generic'),
        canClose: true,
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [selected, onAdd, onClose, toast, t, groupName]);

  if (!isOpen) return null;

  const renderList = () => {
    if (isLoading && candidates.length === 0) return <Skeleton height="44px" borderRadius="6px" />;
    if (error) return <p className="py-6 text-center text-sm text-content-muted">{error}</p>;
    if (candidates.length === 0) {
      return (
        <p className="py-6 text-center text-sm text-content-muted">
          {search ? t('groups.addMembers.noMatch') : t('groups.addMembers.empty')}
        </p>
      );
    }
    return (
      <ul className="flex flex-col divide-y divide-edge">
        {candidates.map((member) => (
          <li key={member.id} className="flex items-center gap-3 py-2">
            <Checkbox
              size="small"
              checked={selected.has(member.id)}
              onChange={() => toggle(member.id)}
              aria-label={t('groups.addMembers.selectAria', { name: member.name })}
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-content">{member.name}</span>
              <span className="truncate text-xs text-content-muted">{member.email}</span>
            </div>
            <StatusBadge status={member.status} />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      closeDisabled={submitting}
      closeAriaLabel={t('common.close')}
      useBodyPortal
      title={t('groups.addMembers.title')}
      subtitle={t('groups.addMembers.subtitle', { name: groupName })}
      panelClassName={NO_HEADER_LINE}
      maxWidthClassName={WIDE_MODAL}
    >
      <div className="flex flex-col gap-3 px-6 py-4">
        <SearchInput
          aria-label={t('groups.addMembers.searchAria')}
          placeholder={t('groups.addMembers.searchPlaceholder')}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          onClear={handleClearSearch}
        />
        <div className="scrollbar-aimsos-auto-sm max-h-80 overflow-y-auto">{renderList()}</div>
      </div>
      <div className="flex justify-end gap-2 border-t border-edge px-6 py-4">
        <Button variant="secondary" size="md" disabled={submitting} onClick={handleClose}>
          {t('groups.addMembers.cancel')}
        </Button>
        <Button
          size="md"
          disabled={selected.size === 0}
          loading={submitting}
          onClick={handleAdd}
        >
          {selected.size === 0
            ? t('groups.detail.addMembers')
            : t('groups.addMembers.add', { count: selected.size })}
        </Button>
      </div>
    </ModalLayout>
  );
}

export default AddMembersModal;
